export type OperacaoDoMembro = {
  hoje: number;
  concluidasHoje: number;
  noMes: number;
  emAtendimentoDesde: string | Date | null;
  proxima: { startTime: string; endTime: string; patientName: string | null; patientId: string | null } | null;
};

export type Expediente = { startHour: string; endHour: string; workingDays: number[] } | null;

export type Situacao = {
  chave: "atendendo" | "proxima" | "sem-mais" | "sem-agenda" | "fora-expediente" | "sem-expediente";
  rotulo: string;
  /** Cor semântica discreta — texto sobre fundo tênue, nunca bloco cheio. */
  cor: string;
  fundo: string;
};

const AGORA_HHMM = (d: Date) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/**
 * Traduz os números do dia numa frase que responde "dá pra contar com essa pessoa agora?".
 *
 * A ordem das perguntas é a ordem da operação: primeiro o que está acontecendo (fato carimbado),
 * depois o que vem a seguir, depois o que já acabou, e só então o expediente — que explica a agenda
 * vazia, não invalida o que está em curso.
 *
 * "Fora do expediente" só sai quando existe expediente CONFIGURADO. Sem configuração a resposta
 * honesta é "Expediente não configurado": concluir que alguém está fora de um horário que ninguém
 * definiu é inventar uma regra que a clínica não escreveu.
 */
export function situacaoDoDia(
  op: OperacaoDoMembro | undefined,
  expediente: Expediente,
  agora = new Date(),
): Situacao {
  if (op?.emAtendimentoDesde) {
    return { chave: "atendendo", rotulo: "Atendendo agora", cor: "#0891b2", fundo: "#ecfeff" };
  }

  if (op?.proxima) {
    const rotulo = op.proxima.startTime
      ? `Próxima consulta às ${op.proxima.startTime}`
      : "Tem consulta mais tarde";
    return { chave: "proxima", rotulo, cor: "#2457ff", fundo: "#eef2ff" };
  }

  if (op && op.hoje > 0) {
    // Teve consultas hoje e nenhuma pela frente: o dia dessa pessoa acabou. Vale mesmo fora do
    // horário — o que aconteceu, aconteceu.
    return { chave: "sem-mais", rotulo: "Sem mais consultas hoje", cor: "#16a34a", fundo: "#f0fdf4" };
  }

  if (!expediente) {
    return {
      chave: "sem-expediente",
      rotulo: "Expediente não configurado",
      cor: "#a16207",
      fundo: "#fefce8",
    };
  }

  const hhmm = AGORA_HHMM(agora);
  const foraDoExpediente =
    !expediente.workingDays.includes(agora.getDay()) ||
    hhmm < expediente.startHour ||
    hhmm >= expediente.endHour;

  return foraDoExpediente
    ? { chave: "fora-expediente", rotulo: "Fora do expediente", cor: "#71717a", fundo: "#f4f4f5" }
    : { chave: "sem-agenda", rotulo: "Sem agenda hoje", cor: "#71717a", fundo: "#f4f4f5" };
}

/** Como o expediente aparece no cartão de quem não atende. `null` quando não há o que mostrar. */
export function rotuloDeExpediente(expediente: Expediente): string | null {
  if (!expediente) return null;
  return `Expediente ${expediente.startHour} às ${expediente.endHour}`;
}
